import styled, { css } from 'styled-components';
import { ButtonPropsNovo } from './types';

const getButtonColor = (variant: string) => {
  switch (variant) {
    case 'confirm':
      return '#28a745';
    case 'cancel':
      return '#CF9D08';
    case 'clear':
      return '#007BFF';
    case 'error':
      return '#c6303e';
    default:
      return '#8850BF';
  }
};

const resolveColor = (props: ButtonPropsNovo) => {
  if (props.style?.backgroundColor) return props.style.backgroundColor;
  return props.variant ? getButtonColor(props.variant) : '#8850BF';
};

/**
 * Converte hex (#RGB, #RRGGBB, #RRGGBBAA) para rgb(a).
 * Retorna null se não for hex válido.
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const raw = hex.replace('#', '').trim();
  const expand = (s: string) =>
    s
      .split('')
      .map((ch) => ch + ch)
      .join('');
  const full =
    raw.length === 3 || raw.length === 4
      ? expand(raw.slice(0, 3))
      : raw.slice(0, 6);
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return { r, g, b };
};

/**
 * Retorna uma cor com alpha suave para usar no hover/active do ghost.
 * Se a cor não for hex, cai em fallback neutro (claro/escuro).
 */
const ghostBg = (baseColor: string, alpha: number) => {
  const rgb = hexToRgb(baseColor);
  if (rgb) return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
  // Fallbacks quando vier "rgb(...)" ou nome de cor via style:
  // Leve diferença entre temas claros/escuros
  return `color-mix(in srgb, currentColor ${Math.round(
    alpha * 100,
  )}%, transparent)`;
};

export const StyledButtonNovo = styled.button<ButtonPropsNovo>`
  height: ${({ height }) => height || '2.5rem'};
  width: ${({ width }) => width || '9.375rem'};
  border-radius: 0.25rem;
  padding: 0.1875rem 0.75rem;
  cursor: pointer;
  outline: none;
  transition: background-color 0.2s ease, color 0.2s ease, border 0.2s ease,
    box-shadow 0.2s ease, opacity 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Jost', sans-serif;
  line-height: 1.3125rem;
  user-select: none;
  gap: 0.5rem;
  background-clip: padding-box;

  /* Estado desabilitado: sem pointer-events:none para o cursor funcionar */
  ${(props) =>
    props.disabled &&
    css`
      opacity: 0.6;
      cursor: not-allowed;
      /* mantenha os eventos: o próprio atributo disabled já bloqueia clique/foco */
    `}

  /* Também cubra o atributo nativo (garante estilo vindo do DOM) */
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${(props) => {
    const color = resolveColor(props);

    if (props.ghost) {
      return css`
        background-color: transparent !important;
        border: none !important;
        color: ${color};

        &:not(:disabled):hover {
          background-color: ${ghostBg(color, 0.12)};
          opacity: 1;
        }

        &:not(:disabled):active {
          background-color: ${ghostBg(color, 0.2)};
        }

        &:not(:disabled):focus-visible {
          box-shadow: 0 0 0 3px ${ghostBg(color, 0.35)};
        }
      `;
    }

    if (props.outline) {
      return css`
        background-color: transparent !important;
        border: 1px solid ${color} !important;
        color: ${color};

        &:not(:disabled):hover {
          background-color: ${ghostBg(color, 0.08)};
        }

        &:not(:disabled):active {
          background-color: ${ghostBg(color, 0.16)};
        }

        &:not(:disabled):focus-visible {
          box-shadow: 0 0 0 3px ${ghostBg(color, 0.35)};
        }
      `;
    }

    // Sólido (default)
    return css`
      background-color: ${color};
      border: none;
      color: #ffffff;

      &:not(:disabled):hover {
        opacity: 0.92;
      }

      &:not(:disabled):active {
        opacity: 0.85;
      }

      &:not(:disabled):focus-visible {
        box-shadow: 0 0 0 3px ${ghostBg('#000000', 0.25)};
      }
    `;
  }}

  ${(props) =>
    props.iconOnly &&
    css`
      padding: 0;
      line-height: 0;
      width: ${props.width || '2.5rem'};
      justify-content: center;
    `}
`;

export const IconWrapper = styled.span<{
  position?: 'left' | 'right';
  iconOnly?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  background-color: transparent !important;

  ${({ position, iconOnly }) =>
    !iconOnly &&
    css`
      margin-right: ${position === 'left' ? '0.5rem' : 0};
      margin-left: ${position === 'right' ? '0.5rem' : 0};
    `}
`;
